<script setup lang="ts" generic="T extends Record<string, string>">
import { ref } from 'vue';
import { NxTimeInput } from '../Inputs';
import { defaultEditCellFocusRef, onEditCellKeydown, PartialOrNull } from './internal';
import { default as NxTableColumn, type NxTableColumnProps } from './NxTableColumn.vue';

const props = withDefaults(
  defineProps<
    Omit<NxTableColumnProps<T, keyof T, string>, 'toCsvString'> & {
      /** value that will be used for the csv export */
      toCsvString?: NxTableColumnProps<T, keyof T, string>['toCsvString'];
    }
  >(),
  {
    filterable: true,
    resizeable: true,
    reorderable: true,
    sortable: true,
    editable: true,
    defaultVisible: true,
    layout: 'row',
    toCsvString: undefined,
  }
);

function toCsvString_(rowData: T, rowIndex: number) {
  if (props.toCsvString) {
    return props.toCsvString(rowData, rowIndex);
  }
  return rowData[props.columnId];
}

const modelValue = ref<string | null>(null);
function onQueryLoaded(value: string[]) {
  modelValue.value = value.length > 0 ? value[0] : null;
}
</script>

<template>
  <!-- @vue-generic {T, keyof T, string} -->
  <NxTableColumn
    v-bind="{
      ...props,
      filterKind: props.filterKind ?? 'time',
      onQueryLoaded: props.onQueryLoaded ? props.onQueryLoaded : onQueryLoaded,
      toCsvString: toCsvString_,
    }"
  >
    <template v-if="$slots.summaryCell" #summaryCell="summaryProps">
      <slot name="summaryCell" v-bind="summaryProps"></slot>
    </template>
    <template v-if="$slots.headerCell" #headerCell>
      <slot name="headerCell"></slot>
    </template>
    <template
      #filterCell="{
        setFilterValue,
        disabled,
        registerFocus,
        focusAbove,
        focusBelow,
        focusLeft,
        focusRight,
      }"
    >
      <slot
        name="filterCell"
        v-bind="{
          setFilterValue,
          disabled,
          registerFocus,
          focusAbove,
          focusBelow,
          focusLeft,
          focusRight,
        }"
      >
        <NxTimeInput
          :ref="defaultEditCellFocusRef(registerFocus)"
          height="xs"
          :hour-cycle="24"
          :model-value="modelValue"
          :disabled="disabled"
          @update:model-value="
            (time: string | null) => {
              setFilterValue(time !== null ? [time] : []);
              modelValue = time;
            }
          "
          @keydown="
            (e: KeyboardEvent) =>
              onEditCellKeydown(e, {
                focusAbove,
                focusBelow,
                focusLeft,
                focusRight,
                inputType: 'text',
              })
          "
        ></NxTimeInput>
      </slot>
    </template>
    <template #cell="{ rowData, rowIndex }">
      <slot name="cell" v-bind="{ rowData, rowIndex }">
        <span class="one-line">
          {{ toCsvString_(rowData, rowIndex) }}
        </span>
      </slot>
    </template>
    <template #createCell="{ partialRowData, setPartialRowData }">
      <slot name="createCell" v-bind="{ partialRowData, setPartialRowData }">
        <!-- Same defect and same fix as the date column: a time field renders
             its model, so a hard-coded `null` hid what the user had picked while
             the value itself reached `onCreate` (I-1160). -->
        <NxTimeInput
          :hour-cycle="24"
          :model-value="partialRowData[props.columnId] ?? null"
          @update:model-value="
            (date: string | null) => {
              setPartialRowData({ [props.columnId]: date } as PartialOrNull<T>);
            }
          "
        ></NxTimeInput>
      </slot>
    </template>
    <template
      #editCell="{
        rowData,
        rowIndex,
        onEdit,
        registerFocus,
        focusAbove,
        focusBelow,
        focusLeft,
        focusRight,
      }"
    >
      <slot
        name="editCell"
        v-bind="{
          rowData,
          rowIndex,
          onEdit,
          registerFocus,
          focusAbove,
          focusBelow,
          focusLeft,
          focusRight,
        }"
      >
        <div class="nx-ghost-cell">
          <NxTimeInput
            :ref="defaultEditCellFocusRef(registerFocus)"
            :hour-cycle="24"
            :model-value="rowData[props.columnId]"
            @update:model-value="
              (date: string | null) => {
                onEdit({ [props.columnId]: date } as Partial<T>);
              }
            "
            @keydown="
              (e: KeyboardEvent) =>
                onEditCellKeydown(e, {
                  focusAbove,
                  focusBelow,
                  focusLeft,
                  focusRight,
                  // TODO: check if correct
                  inputType: 'text',
                })
            "
          ></NxTimeInput>
        </div>
      </slot>
    </template>
  </NxTableColumn>
</template>

<style scoped>
.one-line {
  min-width: 0;
  overflow: hidden;
  white-space: nowrap;
  /* Cut long values with an ellipsis, never mid-letter. */
  text-overflow: ellipsis;
}
</style>
